import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useTerminologia } from '../hooks/useTerminologia';

const MisPaquetes = () => {
    const toast    = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const t = useTerminologia();
    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');
    const usuarioId = JSON.parse(localStorage.getItem('user') || '{}').id
        || JSON.parse(localStorage.getItem('user') || '{}').usuarioId
        || 0;

    const [paquetes,          setPaquetes]          = useState([]);
    const [ufs,               setUfs]               = useState([]);
    const [ufSeleccionada,    setUfSeleccionada]    = useState(null);
    const [loading,           setLoading]           = useState(false);

    // ─── Carga sólo las UFs asociadas al usuario logueado ────────────────────────

    useEffect(() => {
        if (!barrio?.id || !usuarioId) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => {
                setUfs(data);
                if (data.length === 1) setUfSeleccionada(data[0].unidadFuncionalId);
            })
            .catch(() => {});
    }, [HOST_API, barrio?.id, usuarioId]);

    // ─── Fetch paquetes ───────────────────────────────────────────────────────────

    const fetchPaquetes = useCallback(async () => {
        if (!barrio?.id || !ufSeleccionada) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/paquetes/GetPaquetes?administracionId=${administracionId}&barrioId=${barrio.id}&unidadFuncionalId=${ufSeleccionada}`
            );
            setPaquetes(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los paquetes', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, ufSeleccionada]);

    useEffect(() => { fetchPaquetes(); }, [fetchPaquetes]);

    // ─── Render helpers ───────────────────────────────────────────────────────────

    const fmtFecha = (val) => val
        ? new Date(val).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
        : '—';

    const estadoBadge = (row) => row.entregado
        ? <Tag value="Entregado" severity="success" style={{ fontSize: '0.72rem' }} />
        : <Tag value="Pendiente" severity="warning" style={{ fontSize: '0.72rem' }} />;

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Mis Paquetes</h4>
            <br />
            <Toast ref={toast} />
            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* ─── Selector de UF ──────────────────────────────────────────────── */}
            <div className="flex align-items-end gap-3 mb-4 flex-wrap">
                <div>
                    <label style={lbl}>{t.UnidadFuncional}</label>
                    <Dropdown
                        value={ufSeleccionada}
                        options={ufs}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfSeleccionada(e.value)}
                        placeholder="Seleccioná tu UF"
                        filter
                        className="p-inputtext-sm"
                        style={{ width: 220 }}
                    />
                </div>
                <Button label="Buscar" icon="pi pi-search" onClick={fetchPaquetes} className="p-button-sm" disabled={!ufSeleccionada} />
            </div>

            {/* ─── Tabla ───────────────────────────────────────────────────────── */}
            {ufSeleccionada ? (
                <div className="grid">
                    <div className="col-12">
                        <DataTable
                            value={paquetes}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 25]}
                            showGridlines
                            responsiveLayout="scroll"
                            emptyMessage="No hay paquetes para esta unidad funcional"
                            className="p-datatable-gridlines bg-white"
                            sortField="fechaRecepcion"
                            sortOrder={-1}
                        >
                            <Column field="nroPedido"      header="Nro. Pedido"    sortable style={{ fontWeight: 500 }} />
                            <Column field="origenEnvio"    header="Origen"         sortable style={{ width: '10rem' }} />
                            <Column field="destinatario"   header="Destinatario"   sortable />
                            <Column field="encargadoRecibe" header="Recibido por"  sortable style={{ width: '12rem' }} />
                            <Column field="fechaRecepcion" header="Fecha llegada"  sortable body={(row) => fmtFecha(row.fechaRecepcion)} style={{ width: '11rem' }} />
                            <Column field="fechaEntrega"   header="Fecha entrega"  sortable body={(row) => fmtFecha(row.fechaEntrega)} style={{ width: '11rem' }} />
                            <Column field="nombreReceptor" header="Retirado por"   sortable style={{ width: '11rem' }} body={(row) => row.nombreReceptor || '—'} />
                            <Column field="observaciones"  header="Observaciones"  body={(row) => row.observaciones || '—'} />
                            <Column header="Estado" style={{ width: '8rem' }} body={estadoBadge} />
                        </DataTable>
                    </div>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#888', fontSize: '0.95rem' }}>
                    Seleccioná tu unidad funcional para ver los paquetes
                </div>
            )}
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default MisPaquetes;
